import {useField} from 'formik'
import {FC, PropsWithChildren} from 'react'
import {useMutation} from 'react-query'

import {useAffiliateContent} from '@/content/affiliate'
import {useFormSummaryContent} from '@/content/formSummary'
import {useTranslation} from '@/i18n'
import {REST_METHODS} from '@/utils/api/types'
import {Currency} from '@/utils/currency'
import {DiscountCouponChange} from '@/utils/discount-coupon/types'

import {SnackbarType} from '../../snackbar/constants'
import {useSnackbarContext} from '../../snackbar/context/SnackbarContext'
import {FormSummaryItem} from '../summary/common/layout/FormSummaryItem'
import {FormSummarySection} from '../summary/common/layout/FormSummarySection'

type VerifiedDiscountCouponProps = {
  removeDiscountCouponUrl?: string | null
  couponId: string
  price: number
  currency?: Currency
  onDiscountCouponChanged?: (change: DiscountCouponChange) => void
  allowRemoval?: boolean
  textColor?: string
  isPriceHighlighted?: boolean
}

export const VerifiedDiscountCoupon: FC<PropsWithChildren<VerifiedDiscountCouponProps>> = ({
  removeDiscountCouponUrl,
  couponId,
  price,
  currency,
  onDiscountCouponChanged,
  textColor,
  isPriceHighlighted,
  children,
  allowRemoval = false,
}) => {
  const {t} = useTranslation()
  const {discountCouponTitle} = useFormSummaryContent()
  const {removedDiscountDescription} = useAffiliateContent()
  const [, , {setValue: setParentFormDiscountCouponId}] = useField('discount_coupon_id')
  const {showSnackbar} = useSnackbarContext()

  const handleCouponRemovalSuccess = (change: DiscountCouponChange) => {
    onDiscountCouponChanged?.(change)
    setParentFormDiscountCouponId(null, false)
    showSnackbar(t(removedDiscountDescription), true)
  }

  const removeCouponMutation = useMutation({
    mutationFn: async (): Promise<DiscountCouponChange> => {
      const response = await fetch(removeDiscountCouponUrl!, {
        method: REST_METHODS.DELETE,
      })

      if (!response.ok) {
        throw new Error('Failed to remove coupon')
      }

      const data = await response.json()

      return {coupon: null, serviceRequest: data?.serviceRequest, quote: data?.quote}
    },
    onSuccess: handleCouponRemovalSuccess,
    onError: () => {
      showSnackbar('error-couldnt-apply', true, SnackbarType.error)
    },
  })

  const handleRemoveCoupon = () => {
    if (!removeDiscountCouponUrl) {
      handleCouponRemovalSuccess({coupon: null})
      return
    }

    removeCouponMutation.mutate()
  }

  return (
    <FormSummarySection label={t(discountCouponTitle)}>
      <FormSummaryItem
        title={couponId}
        price={price}
        currency={currency}
        onRemove={allowRemoval ? handleRemoveCoupon : undefined}
        textColor={textColor}
        isPriceHighlighted={isPriceHighlighted}
      />
      {children}
    </FormSummarySection>
  )
}
